import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    TablePagination,
    Chip,
    InputAdornment,
    Tooltip,
    Divider
} from "@mui/material";

import {
    Visibility,
    Edit,
    Delete,
    Search,
    SwapHoriz
} from "@mui/icons-material";

import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    onSnapshot,
    query,
    where,
    orderBy
} from "firebase/firestore";

import { db } from "../../firebase";
import Swal from "sweetalert2";

function Customeracc() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    const [customers, setCustomers] = useState([]);
    const [trips, setTrips] = useState([]);
    const [payments, setPayments] = useState([]);
    const [payModes, setPayModes] = useState([]);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    const [openHistory, setOpenHistory] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerPayments, setCustomerPayments] = useState([]);

    const [openPayment, setOpenPayment] = useState(false);
    const [editPaymentId, setEditPaymentId] = useState(null);

    const [openTransfer, setOpenTransfer] = useState(false);
    const [transferFrom, setTransferFrom] = useState(null);
    const [transferToId, setTransferToId] = useState("");
    const [isTransferring, setIsTransferring] = useState(false);

    const [openCustEdit, setOpenCustEdit] = useState(false);
    const [custForm, setCustForm] = useState({ customerName: "", mobile: "" });
    const [editingCustId, setEditingCustId] = useState(null);

    const [paymentForm, setPaymentForm] = useState({
        amount: "",
        paymentMode: "",
        note: "",
        date: ""
    });

    useEffect(() => {
        const unsubCustomers = onSnapshot(query(collection(db, "customers"), orderBy("customerName", "asc")), (snap) => {
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubTrips = onSnapshot(collection(db, "trips"), snap => {
            setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubPayments = onSnapshot(collection(db, "customer_history"), snap => {
            setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubCustomers();
            unsubTrips();
            unsubPayments();
            unsubPayModes();
        };
    }, []);

    const computedCustomers = useMemo(() => {
        return customers.map(customer => {
            const totalTrips = trips.filter(t => t.customerId === customer.id).length;
            const totalSale = trips
                .filter(t => t.customerId === customer.id)
                .reduce((sum, trip) => sum + (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0), 0);
            const totalPaid = payments
                .filter(p => p.customerId === customer.id)
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);
            const remaining = totalSale - totalPaid;

            return { ...customer, totalTrips, totalSale, totalPaid, remaining };
        });
    }, [customers, trips, payments]);

    const filteredCustomers = useMemo(() => {
        const searchTerm = search.toLowerCase();
        return computedCustomers.filter(c =>
            c.customerName?.toLowerCase().includes(searchTerm) ||
            c.mobile?.toLowerCase().includes(searchTerm)
        );
    }, [computedCustomers, search]);

    const getPaymentMode = (id) => payModes.find(m => m.payMode === id)?.payMode || "";

    const sortPaymentsDesc = (paymentList) => {
        return [...paymentList].sort((a, b) => {
            const timeA = a.date?.toMillis ? a.date.toMillis() : new Date(a.date || 0).getTime();
            const timeB = b.date?.toMillis ? b.date.toMillis() : new Date(b.date || 0).getTime();
            return timeB - timeA;
        });
    };

    const reloadCustomerPayments = async (customerId) => {
        const paymentSnap = await getDocs(collection(db, "customer_history"));
        const allPayments = paymentSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPayments(allPayments);
        setCustomerPayments(sortPaymentsDesc(allPayments.filter(p => p.customerId === customerId)));
    };

    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setCustomerPayments(sortPaymentsDesc(payments.filter(p => p.customerId === customer.id)));
        setOpenHistory(true);
    };

    const handleSavePayment = async () => {
        if (!paymentForm.amount || !paymentForm.date || !paymentForm.paymentMode) {
            return Toast.fire({ icon: "error", title: "Fill required fields" });
        }

        const paymentAmount = Number(paymentForm.amount);
        const data = {
            ...paymentForm,
            amount: paymentAmount,
            customerId: selectedCustomer.id,
            date: Timestamp.fromDate(new Date(paymentForm.date)),
            type: "Account",
        };

        try {
            if (editPaymentId) {
                await updateDoc(doc(db, "customer_history", editPaymentId), data);
            } else {
                await addDoc(collection(db, "customer_history"), data);
            }
            const paymentSnap = await getDocs(collection(db, "customer_history"));
            const allPayments = paymentSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            setPayments(allPayments);
            setCustomerPayments(sortPaymentsDesc(allPayments.filter(p => p.customerId === selectedCustomer.id)));
            const totalSale = trips
                .filter(t => t.customerId === selectedCustomer.id)
                .reduce((sum, trip) => sum + (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0), 0);
            const totalPaid = allPayments
                .filter(p => p.customerId === selectedCustomer.id)
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);
            const updatedRemaining = totalSale - totalPaid;

            setOpenPayment(false);

            await sendPaymentWhatsAppMessage({
                mobile: selectedCustomer.mobile,
                customerName: selectedCustomer.customerName,
                amount: paymentAmount,
                date: paymentForm.date,
                paymentMode: paymentForm.paymentMode,
                remainingBalance: updatedRemaining
            });

        } catch (error) {
            console.error("Error saving payment:", error);
            Swal.fire("Error", "Failed to save payment", "error");
        }
    };

    const handleDeletePayment = async (id) => {
        if (window.confirm("Are you sure you want to delete this payment?")) {
            await deleteDoc(doc(db, "customer_history", id));
            await reloadCustomerPayments(selectedCustomer.id);
            Toast.fire({ icon: "success", title: "Deleted" });
        }
    };

    const handleTransferHistory = async () => {
        if (!transferToId) return Swal.fire("Error", "Select target customer", "error");
        const target = customers.find(c => c.id === transferToId);
        if (!window.confirm(`Transfer EVERYTHING to ${target.customerName}?`)) return;

        setIsTransferring(true);
        try {
            const tripsSnap = await getDocs(query(collection(db, "trips"), where("customerId", "==", transferFrom.id)));
            const paySnap = await getDocs(query(collection(db, "customer_history"), where("customerId", "==", transferFrom.id)));
            const promises = [];

            tripsSnap.docs.forEach(d => promises.push(updateDoc(doc(db, "trips", d.id), { customerId: target.id, customerName: target.customerName })));
            paySnap.docs.forEach(d => promises.push(updateDoc(doc(db, "customer_history", d.id), { customerId: target.id })));

            await Promise.all(promises);
            setOpenTransfer(false);
            setTransferToId("");
            Toast.fire({ icon: "success", title: "Transferred Successfully" });
        } catch (error) {
            Swal.fire("Error", "Transfer failed", "error");
        } finally {
            setIsTransferring(false);
        }
    };

    const handleEditCustomer = (customer) => {
        setEditingCustId(customer.id);
        setCustForm({ customerName: customer.customerName || "", mobile: customer.mobile || "" });
        setOpenCustEdit(true);
    };

    const handleUpdateCustomer = async () => {
        if (!custForm.customerName) return Toast.fire({ icon: "error", title: "Name required" });
        try {
            await updateDoc(doc(db, "customers", editingCustId), custForm);
            setOpenCustEdit(false);
            Toast.fire({ icon: "success", title: "Customer updated" });
        } catch (error) {
            Swal.fire("Error", "Update failed", "error");
        }
    };

    const handleDeleteCustomer = async (customer) => {
        if (window.confirm(`Permanently delete ${customer.customerName}?`)) {
            try {
                await deleteDoc(doc(db, "customers", customer.id));
                Toast.fire({ icon: "success", title: "Customer deleted" });
            } catch (error) {
                Swal.fire("Error", "Could not delete", "error");
            }
        }
    };

    const sendPaymentWhatsAppMessage = async ({
        mobile,
        customerName,
        amount,
        date,
        paymentMode,
        remainingBalance
    }) => {
        if (!mobile) {
            console.warn("WhatsApp receipt skipped: Customer mobile number is missing.");
            return;
        }

        try {
            const wabaid = "1174916145695973";
            const accesstoken = "EAAKj5T0Mx1YBRNd6VhhufPP2ApIjZCsZBFOkl855np9VZBvPvZAZAj7axVAevZBiapxd1Iw38v0hhZBwB2knNeLYSVgjR3RqtPn164H2GQ3ljP46kQvAAq0gsl3lfKDh55ZCsmo5fGZCaUSPpCRqeFjVA6VUeIaiu0ZCNxPPMTsV4oxnnUz4TRoICdbiW0xsAncwZDZD";
            const formattedDate = date ? new Date(date).toLocaleDateString("en-IN") : new Date().toLocaleDateString("en-IN");
            const cleanMobile = mobile;

            const payload = {
                messaging_product: "whatsapp",
                to: "91" + cleanMobile,
                type: "template",
                template: {
                    name: "customer_pay", // Replace with your exact Meta template name
                    language: { code: "en" },
                    components: [
                        {
                            type: "body",
                            parameters: [
                                { type: "text", text: String(customerName || "Customer") },        // {{1}} Customer Name
                                { type: "text", text: String(Math.round(Number(amount || 0))) },   // {{2}} Paid Amount
                                { type: "text", text: String(formattedDate) },                     // {{3}} Payment Date
                                { type: "text", text: String(paymentMode || "Cash") },             // {{4}} Payment Mode
                                { type: "text", text: String(Math.round(Number(remainingBalance || 0))) } // {{5}} Remaining Balance
                            ]
                        }
                    ]
                }
            };

            const response = await fetch(`https://graph.facebook.com/v22.0/${wabaid}/messages`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accesstoken}`
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (response.ok) {
                await addDoc(collection(db, "whatsapp_history"), {
                    mobile: cleanMobile,
                    customerName,
                    type: "Payment Receipt",
                    amount: Number(amount),
                    status: "Sent",
                    createdAt: Timestamp.now()
                });
                Toast.fire({ icon: "success", title: "Payment Saved & WhatsApp Sent" });
            } else {
                await addDoc(collection(db, "whatsapp_history"), {
                    mobile: cleanMobile,
                    customerName,
                    type: "Payment Receipt",
                    amount: Number(amount),
                    status: "Failed",
                    error: data.error?.message,
                    createdAt: Timestamp.now()
                });
            }
        } catch (error) {
            console.error("❌ WhatsApp Payment Notification Error:", error);
        }
    };

    const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

    // Custom tight cell padding
    const tightCell = { py: 0.5, px: 1.5 };

    return (
        <Box p={2} sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>

            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Accounts
                </Typography>
                <TextField
                    size="small"
                    placeholder="Search..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: "250px", bgcolor: "white", borderRadius: 1, '& .MuiInputBase-root': { height: '32px' } }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                    }}
                />
            </Box>

            {/* Main Table */}
            <Paper elevation={1} sx={{ borderRadius: 1.5, overflow: "hidden" }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#1976d2" }}>
                        <TableRow>
                            <TableCell sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>#</TableCell>
                            <TableCell sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Customer</TableCell>
                            <TableCell align="center" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Trips</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Sale</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Paid</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Bal</TableCell>
                            <TableCell align="center" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredCustomers.length === 0 ? (
                            <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}>No data</TableCell></TableRow>
                        ) : (
                            filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c, i) => (
                                <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: "#f1f5f9" } }}>
                                    <TableCell sx={tightCell}>{page * rowsPerPage + i + 1}</TableCell>
                                    <TableCell sx={tightCell}>
                                        <Typography variant="body2" fontWeight="600">{c.customerName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{c.mobile || "-"}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={tightCell}>
                                        <Chip label={c.totalTrips} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Typography variant="body2">{formatMoney(c.totalSale)}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Typography variant="body2" color="success.main" fontWeight="500">
                                            {formatMoney(c.totalPaid)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Chip
                                            label={formatMoney(c.remaining)}
                                            color={c.remaining > 0 ? "error" : "success"}
                                            sx={{ height: 22, fontWeight: 'bold', fontSize: '0.75rem', borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={tightCell}>
                                        <Box display="flex" gap={0.5} justifyContent="center">
                                            <Tooltip title="View"><IconButton size="small" color="primary" onClick={() => handleView(c)} sx={{ p: 0.5, bgcolor: '#e3f2fd' }}><Visibility fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Edit"><IconButton size="small" color="info" onClick={() => handleEditCustomer(c)} sx={{ p: 0.5, bgcolor: '#e1f5fe' }}><Edit fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Transfer"><IconButton size="small" color="warning" onClick={() => { setTransferFrom(c); setOpenTransfer(true); }} sx={{ p: 0.5, bgcolor: '#fff3e0' }}><SwapHoriz fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteCustomer(c)} sx={{ p: 0.5, bgcolor: '#ffebee' }}><Delete fontSize="small" /></IconButton></Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={filteredCustomers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)} // 👈 Changed 'new' to 'newPage'
                    sx={{ borderTop: "1px solid #e0e0e0", minHeight: '40px', '.MuiTablePagination-toolbar': { minHeight: '40px' } }}
                />
            </Paper>

            {/* PAYMENT HISTORY DIALOG */}
            <Dialog open={openHistory} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ bgcolor: "#1976d2", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, px: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">{selectedCustomer?.customerName}</Typography>
                        <Chip label="History" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 20 }} />
                    </Box>
                    <Button variant="contained" color="success" size="small" disableElevation onClick={() => {
                        setEditPaymentId(null);
                        setPaymentForm({ amount: "", paymentMode: "", note: "", date: new Date().toISOString().split("T")[0] });
                        setOpenPayment(true);
                    }}>+ Add</Button>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                            <TableRow>
                                <TableCell sx={tightCell}>Date</TableCell>
                                <TableCell sx={tightCell}>Note</TableCell>
                                <TableCell sx={tightCell}>Mode</TableCell>
                                <TableCell align="right" sx={tightCell}>Amount</TableCell>
                                <TableCell align="center" sx={tightCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerPayments.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}>No history</TableCell></TableRow>
                            ) : (
                                customerPayments.map(p => (
                                    <TableRow key={p.id} hover>
                                        <TableCell sx={tightCell}>{p.date?.toDate ? p.date.toDate().toLocaleDateString('en-GB') : "N/A"}</TableCell>
                                        <TableCell sx={tightCell}><Typography variant="body2">{p.note || "-"}</Typography></TableCell>
                                        <TableCell sx={tightCell}><Chip label={getPaymentMode(p.paymentMode)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} /></TableCell>
                                        <TableCell align="right" sx={tightCell}><Typography variant="body2" fontWeight="bold" color="success.main">{formatMoney(p.amount)}</Typography></TableCell>
                                        <TableCell align="center" sx={tightCell}>
                                            <IconButton size="small" color="primary" sx={{ p: 0.5 }} onClick={() => {
                                                setEditPaymentId(p.id);
                                                setPaymentForm({ amount: p.amount, paymentMode: p.paymentMode, note: p.note || "", date: p.date?.toDate().toISOString().split("T")[0] });
                                                setOpenPayment(true);
                                            }}><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" sx={{ p: 0.5 }} onClick={() => handleDeletePayment(p.id)}><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions sx={{ p: 1, borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setOpenHistory(false)} size="small" color="inherit">Close</Button>
                </DialogActions>
            </Dialog>

            {/* ADD / EDIT PAYMENT DIALOG */}
            <Dialog open={openPayment} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 'bold' }}>
                    {editPaymentId ? "Edit Payment" : "New Payment"}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField label="Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={paymentForm.date} onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })} />
                    <TextField label="Amount (₹)" type="number" size="small" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                    <TextField select label="Mode" size="small" value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}>
                        {payModes.map(m => <MenuItem key={m.id} value={m.payMode} sx={{ fontSize: '0.85rem' }}>{m.payMode}</MenuItem>)}
                    </TextField>
                    <TextField label="Note" size="small" value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setOpenPayment(false)} size="small" color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSavePayment} size="small" disableElevation>Save</Button>
                </DialogActions>
            </Dialog>

            {/* TRANSFER DIALOG */}
            <Dialog open={openTransfer} onClose={() => !isTransferring && setOpenTransfer(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}>Transfer Account</DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2 }}>
                    <Box bgcolor="#f8fafc" p={1.5} borderRadius={1} mb={2} border="1px solid #e2e8f0">
                        <Typography variant="caption" color="text.secondary">From:</Typography>
                        <Typography variant="body2" fontWeight="bold" mb={0.5}>{transferFrom?.customerName}</Typography>
                        <Box display="flex" justifyContent="space-between">
                            <Typography variant="caption">Trips: {computedCustomers.find(c => c.id === transferFrom?.id)?.totalTrips || 0}</Typography>
                            <Typography variant="caption" color="error" fontWeight="bold">Bal: {formatMoney(computedCustomers.find(c => c.id === transferFrom?.id)?.remaining || 0)}</Typography>
                        </Box>
                    </Box>
                    <TextField select label="To Target Customer" size="small" fullWidth value={transferToId} onChange={(e) => setTransferToId(e.target.value)} disabled={isTransferring}>
                        {computedCustomers.filter(c => c.id !== transferFrom?.id).sort((a, b) => a.customerName.localeCompare(b.customerName)).map((m) => (
                            <MenuItem key={m.id} value={m.id} sx={{ fontSize: '0.85rem' }}>{m.customerName}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setOpenTransfer(false)} disabled={isTransferring} size="small" color="inherit">Cancel</Button>
                    <Button variant="contained" color="warning" onClick={handleTransferHistory} disabled={isTransferring} size="small" disableElevation>
                        {isTransferring ? "Processing..." : "Transfer"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* EDIT CUSTOMER DIALOG */}
            <Dialog open={openCustEdit} onClose={() => setOpenCustEdit(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ py: 1.5, fontSize: '1rem', fontWeight: 'bold' }}>Edit Details</DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                    <TextField label="Name" size="small" value={custForm.customerName} onChange={(e) => setCustForm({ ...custForm, customerName: e.target.value })} />
                    <TextField label="Mobile" size="small" value={custForm.mobile} onChange={(e) => setCustForm({ ...custForm, mobile: e.target.value })} />
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setOpenCustEdit(false)} size="small" color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateCustomer} size="small" disableElevation>Update</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Customeracc;